import { AxiosError } from 'axios';
import { HTTP_STATUS } from '@stx-shared/constants';
import type { LoginFormData } from '../schemas';
import { VALIDATION_MESSAGES } from '../constants/index';

function parseNetworkError(error: AxiosError): string {
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return VALIDATION_MESSAGES.API.NETWORK_ERROR;
  }

  if (error.message?.includes('timeout')) {
    return 'Connection timeout. Please check your network and try again.';
  }

  return VALIDATION_MESSAGES.API.NETWORK_ERROR;
}

function parseResponseError(error: AxiosError): string {
  const status = error.response?.status;
  const data = error.response?.data as
    | {
        error?: { message?: string };
        message?: string;
      }
    | undefined;

  if (data?.error?.message) {
    return data.error.message;
  }

  if (status === HTTP_STATUS.UNAUTHORIZED) {
    return VALIDATION_MESSAGES.API.LOGIN_FAILED;
  }

  if (
    typeof status === 'number' &&
    status >= HTTP_STATUS.INTERNAL_SERVER_ERROR
  ) {
    return VALIDATION_MESSAGES.API.SERVER_ERROR;
  }

  if (data?.message) {
    return data.message;
  }

  return VALIDATION_MESSAGES.API.UNKNOWN_ERROR;
}

/**
 * Parse API error response and return user-friendly message
 */
export const parseApiError = (error: unknown): string => {
  if (!error) {
    return VALIDATION_MESSAGES.API.UNKNOWN_ERROR;
  }

  if (error instanceof AxiosError) {
    if (!error.response) {
      return parseNetworkError(error);
    }

    return parseResponseError(error);
  }

  if (error instanceof Error) {
    return error.message || VALIDATION_MESSAGES.API.UNKNOWN_ERROR;
  }

  return VALIDATION_MESSAGES.API.UNKNOWN_ERROR;
};

/**
 * Format company code (trim and uppercase)
 */
export const formatCompanyCode = (value: string): string => {
  return value.trim().toUpperCase();
};

/**
 * Format user ID (trim)
 */
export const formatUserId = (value: string): string => {
  return value.trim();
};

/**
 * Sanitize login form values
 */
export const sanitizeLoginFormValues = (
  values: LoginFormData,
): LoginFormData => {
  return {
    companyCode: formatCompanyCode(values.companyCode),
    userId: formatUserId(values.userId),
    password: values.password, // Don't trim password
    remember: values.remember,
  };
};
